import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateOnly, humanize, money } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable, Dialog, FilterField, PageHeader, SearchFilterBar, StatTile } from '@/components/ui/layout';
import { Button, Field, Input, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PAYOUT_PAYMENT_MODE, type PayoutPaymentMode } from '@his/shared';

interface Payout {
  id: string;
  doctorId: string;
  doctorName: string | null;
  periodStart: string;
  periodEnd: string;
  grossShareMinor: number;
  deductionsMinor: number;
  netPayableMinor: number;
  status: string;
}

interface PayoutDetail extends Payout {
  paidMode?: string | null;
  paidRef?: string | null;
  adjustments?: { id: string; amountMinor: number; reason: string; reference: string | null; createdAt: string }[];
  doctor: { id: string; name: string; designation: string | null } | null;
  summary: { context: string; count: number; baseMinor: number; shareMinor: number }[];
  lines: {
    id: string;
    description: string;
    invoiceNumber: string | null;
    context: string | null;
    baseMinor: number;
    sharePct: number;
    shareMinor: number;
  }[];
}

/**
 * Doctor payouts (SRS 9.5).
 *
 * The advice shows the base each share was computed on, not just the total,
 * because the conversation a consultant wants is always about a specific
 * invoice.
 */
export function PayoutsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState<string | null>(params.get('open'));

  const payouts = useQuery({
    queryKey: ['payouts', status],
    queryFn: () => get<Payout[]>(`/accounting/payouts${status ? `?status=${status}` : ''}`),
  });

  const rows = payouts.data ?? [];

  return (
    <>
      <PageHeader
        title={tr('billing', 'doctor_payouts')}
        description={tr('billing', 'shares_computed_from_money_the_hospital_has')}
        actions={<Button onClick={() => navigate('/accounting/payouts/new')}>{tr('billing', 'compute_a_period')}</Button>}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatTile label={tr('billing', 'draft')} value={rows.filter((p) => p.status === 'draft').length} />
        <StatTile
          label={tr('billing', 'approved_unpaid')}
          value={money(rows.filter((p) => p.status === 'approved').reduce((sum, p) => sum + p.netPayableMinor, 0))}
          tone="warning"
        />
        <StatTile
          label={tr('billing', 'paid_this_list')}
          value={money(rows.filter((p) => p.status === 'paid').reduce((sum, p) => sum + p.netPayableMinor, 0))}
          tone="success"
        />
      </div>

      <SearchFilterBar
        filters={
          <FilterField label={tr('billing', 'status')}>
            <Select className="w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{tr('billing', 'all_statuses')}</option>
              {['draft', 'approved', 'paid'].map((s) => (
                <option key={s} value={s}>
                  {humanize(s)}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={status ? () => setStatus('') : undefined}
      />

      <DataTable
        rows={rows}
        loading={payouts.isLoading} refreshing={payouts.isFetching}
        error={payouts.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => setOpen(row.id)}
        empty={tr('billing', 'no_payouts_computed_yet')}
        columns={[
          { header: tr('billing', 'doctor'), sort: (row) => row.doctorName ?? row.doctorId, cell: (row) => row.doctorName ?? row.doctorId.slice(0, 8) },
          { header: tr('billing', 'period'), sort: (row) => row.periodStart, cell: (row) => `${dateOnly(row.periodStart)} → ${dateOnly(row.periodEnd)}` },
          { header: tr('billing', 'gross_share'), align: 'right', sort: (row) => row.grossShareMinor, cell: (row) => money(row.grossShareMinor) },
          { header: tr('billing', 'deductions'), align: 'right', sort: (row) => row.deductionsMinor, cell: (row) => money(row.deductionsMinor) },
          { header: tr('billing', 'net_payable'), align: 'right', sort: (row) => row.netPayableMinor, cell: (row) => <span className="font-medium">{money(row.netPayableMinor)}</span> },
          { header: tr('billing', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
        ]}
      />

      {open !== null && (
        <AdviceDialog payoutId={open} onClose={() => setOpen(null)} />
      )}
    </>
  );
}

function AdviceDialog({ payoutId, onClose }: { payoutId: string | null; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const advice = useQuery({
    queryKey: ['payout-advice', payoutId],
    queryFn: () => get<PayoutDetail>(`/accounting/payouts/${payoutId}/advice`),
    enabled: Boolean(payoutId),
  });

  const [paidMode, setPaidMode] = useState<PayoutPaymentMode | ''>('');
  const [paidRef, setPaidRef] = useState('');
  const refNeeded = paidMode !== '' && paidMode !== 'cash';
  const readyToMarkPaid = paidMode !== '' && (!refNeeded || paidRef.trim().length > 0);

  const transition = useMutation({
    mutationFn: (status: string) =>
      put(`/accounting/payouts/${payoutId}/status`, status === 'paid' ? { status, paidMode, paidRef: paidRef.trim() || undefined } : { status }),
    onSuccess: (_, status) => {
      toast.success(status === 'approved' ? tr('billing', 'approved_and_booked_to_the_ledger') : tr('billing', 'marked_paid'));
      void queryClient.invalidateQueries({ queryKey: ['payouts'] });
      void queryClient.invalidateQueries({ queryKey: ['payout-advice', payoutId] });
    },
    onError: (error) => toast.error(tr('billing', 'could_not_update_the_payout'), describeError(error)),
  });

  const p = advice.data;

  return (
    <Dialog
      open={Boolean(payoutId)}
      onClose={onClose}
      wide
      title={p ? tr('billing', 'payout_advice_x', { value: p.doctor?.name ?? '' }) : tr('billing', 'payout_advice')}
      description={p ? `${dateOnly(p.periodStart)} → ${dateOnly(p.periodEnd)}` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'close')}
          </Button>
          {p?.status === 'draft' && (
            <Button variant="outline" onClick={() => navigate(`/accounting/payouts/${payoutId}/adjust`)}>
              {tr('billing', 'add_adjustment')}
            </Button>
          )}
          {p?.status === 'draft' && (
            <Button onClick={() => transition.mutate('approved')} loading={transition.isPending}>
              {tr('billing', 'approve')}
            </Button>
          )}
          {p?.status === 'approved' && (
            <Button onClick={() => transition.mutate('paid')} loading={transition.isPending} disabled={!readyToMarkPaid}>
              {tr('billing', 'mark_paid')}
            </Button>
          )}
        </>
      }
    >
      {p && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <StatTile label={tr('billing', 'gross_share')} value={money(p.grossShareMinor)} />
            <StatTile label={tr('billing', 'deductions')} value={money(p.deductionsMinor)} />
            <StatTile label={tr('billing', 'net_payable')} value={money(p.netPayableMinor)} tone="success" />
          </div>

          {p.status === 'approved' && (
            <div className="space-y-2 rounded-md border p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={tr('billing', 'paid_by')} required>
                  <ChoiceButtons<PayoutPaymentMode>
                    layout="chips"
                    value={paidMode}
                    onChange={setPaidMode}
                    options={PAYOUT_PAYMENT_MODE.map((mode) => ({ value: mode, label: humanize(mode, 'payment_mode') }))}
                  />
                </Field>
                <Field label={tr('billing', 'payment_reference')} required={refNeeded} hint={tr('billing', 'reference_needed_for_cheque_transfer_or_wallet')}>
                  <Input value={paidRef} maxLength={120} onChange={(e) => setPaidRef(e.target.value)} />
                </Field>
              </div>
              <p className="text-xs text-muted-foreground">{tr('billing', 'marking_paid_clears_the_payable_against_cash_or_bank')}</p>
            </div>
          )}

          {p.status === 'paid' && p.paidMode && (
            <p className="text-sm text-muted-foreground">
              {tr('billing', 'paid_by')}: {humanize(p.paidMode)}
              {p.paidRef ? ` · ${p.paidRef}` : ''}
            </p>
          )}

          <DataTable
            dense
            rows={p.summary}
            rowKey={(row) => row.context}
            empty={tr('billing', 'nothing_earned_in_this_period')}
            columns={[
              { header: tr('billing', 'where'), cell: (row) => humanize(row.context) },
              { header: tr('billing', 'items'), align: 'right', cell: (row) => row.count },
              { header: tr('billing', 'collected_base'), align: 'right', cell: (row) => money(row.baseMinor) },
              { header: tr('billing', 'share_2'), align: 'right', cell: (row) => money(row.shareMinor) },
            ]}
          />

          <DataTable
            dense
            rows={p.lines}
            rowKey={(row) => row.id}
            empty={tr('billing', 'no_lines')}
            columns={[
              { header: tr('billing', 'invoice'), cell: (row) => row.invoiceNumber ?? '—' },
              { header: tr('billing', 'service'), cell: (row) => row.description },
              { header: tr('billing', 'base'), align: 'right', cell: (row) => money(row.baseMinor) },
              { header: '%', align: 'right', cell: (row) => `${row.sharePct}%` },
              { header: tr('billing', 'share_2'), align: 'right', cell: (row) => money(row.shareMinor) },
            ]}
          />

          {(p.adjustments ?? []).length > 0 && (
            <DataTable
              dense
              rows={p.adjustments}
              rowKey={(row) => row.id}
              columns={[
                { header: tr('billing', 'adjustment'), cell: (row) => row.reason },
                { header: tr('billing', 'reference'), cell: (row) => row.reference ?? '—' },
                { header: tr('billing', 'amount'), align: 'right', cell: (row) => money(row.amountMinor) },
              ]}
            />
          )}
        </div>
      )}
    </Dialog>
  );
}
